// MOD-08 — robots.txt / sitemap yönetim ekranı (RedirectMapPage emsali).
//
// `robots.*` AppSetting'lerini generic settings API'siyle (kategori `seo`)
// okuyup yazar. Public `/sitemap.xml` ve `/robots.txt` belgelerini BE origin'i
// üzerinden yeni sekmede önizler. RBAC: settings.manage (BE PUT deseni).

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Skeleton,
  Switch,
  Typography,
} from 'antd';
import { ExternalLink, Save, ShieldAlert } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { settingsApi } from '@api/settings';
import { apiBaseUrl } from '@api/http';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { SEO_SETTING_KEYS } from '../types';
import type { AppSetting } from '@features/settings/types';

const { Text } = Typography;

interface Draft {
  allowIndexingGlobal: boolean;
  customBody: string;
  sitemapUrl: string;
}

function parseBool(value: string | undefined): boolean {
  return value === 'true' || value === '1';
}

function draftFrom(byKey: Map<string, AppSetting>): Draft {
  return {
    allowIndexingGlobal: parseBool(byKey.get(SEO_SETTING_KEYS.allowIndexingGlobal)?.value),
    customBody: byKey.get(SEO_SETTING_KEYS.customBody)?.value ?? '',
    sitemapUrl: byKey.get(SEO_SETTING_KEYS.sitemapUrl)?.value ?? '',
  };
}

function PreviewLink({ path, label }: { path: string; label: string }) {
  return (
    <Button
      type="link"
      size="small"
      href={`${apiBaseUrl}${path}`}
      target="_blank"
      rel="noopener noreferrer"
      icon={<ExternalLink size={14} />}
      style={{ paddingInline: 0 }}
    >
      {label}
    </Button>
  );
}

export function SeoSettingsPage() {
  const { t } = useTranslation(['web', 'common', 'errors']);
  const canManage = useCan(PERMISSIONS.SETTINGS_MANAGE);
  const qc = useQueryClient();

  const breadcrumb = [
    { label: t('common:labels.home'), to: ROUTES.DASHBOARD },
    { label: 'Web' },
    { label: t('seo.page.breadcrumb') },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ['seo-settings'],
    queryFn: () => settingsApi.list({ category: 'seo' }),
    enabled: canManage,
  });

  const byKey = useMemo(
    () => new Map((data ?? []).map((s) => [s.key, s] as const)),
    [data],
  );

  // Fetched dataset'ten bir kez hidrasyon (effect'siz; rowVersion parmak izine
  // göre tazelenir — kayıt sonrası yeni rowVersion draft'ı güncel tutar).
  const fingerprint = (data ?? [])
    .map((s) => `${s.key}:${s.rowVersion ?? ''}`)
    .join('|');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [hydratedFrom, setHydratedFrom] = useState<string | null>(null);
  if (data && hydratedFrom !== fingerprint) {
    setHydratedFrom(fingerprint);
    setDraft(draftFrom(byKey));
  }

  const missingKeys =
    data != null &&
    (!byKey.has(SEO_SETTING_KEYS.allowIndexingGlobal) ||
      !byKey.has(SEO_SETTING_KEYS.customBody) ||
      !byKey.has(SEO_SETTING_KEYS.sitemapUrl));

  const baseline = data ? draftFrom(byKey) : null;
  const dirty =
    draft != null &&
    baseline != null &&
    (draft.allowIndexingGlobal !== baseline.allowIndexingGlobal ||
      draft.customBody !== baseline.customBody ||
      draft.sitemapUrl !== baseline.sitemapUrl);

  const saveMutation = useMutation({
    mutationFn: async (next: Draft) => {
      const updates: Array<Promise<void>> = [];
      const pushUpdate = (key: string, value: string) => {
        const setting = byKey.get(key);
        if (!setting || setting.value === value) return;
        updates.push(
          settingsApi.update(key, {
            value,
            valueType: setting.valueType,
            category: setting.category,
            isPublic: setting.isPublic,
            description: setting.description,
            rowVersion: setting.rowVersion,
          }),
        );
      };
      pushUpdate(
        SEO_SETTING_KEYS.allowIndexingGlobal,
        next.allowIndexingGlobal ? 'true' : 'false',
      );
      pushUpdate(SEO_SETTING_KEYS.customBody, next.customBody);
      pushUpdate(SEO_SETTING_KEYS.sitemapUrl, next.sitemapUrl);
      await Promise.all(updates);
    },
    onSuccess: async () => {
      toast.success(t('seo.toast.saved'));
      await qc.invalidateQueries({ queryKey: ['seo-settings'] });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  if (!canManage) {
    return (
      <div>
        <PageHeader
          title={t('seo.page.title')}
          description={t('seo.page.description')}
          breadcrumb={breadcrumb}
        />
        <Card>
          <Alert
            type="error"
            showIcon
            icon={<ShieldAlert size={18} />}
            message={t('seo.accessDenied.title')}
            description={t('seo.accessDenied.description')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="seo-settings-page">
      <PageHeader
        title={t('seo.page.title')}
        description={t('seo.page.description')}
        breadcrumb={breadcrumb}
        actions={
          <Space wrap>
            <PreviewLink path="/sitemap.xml" label={t('seo.preview.sitemap')} />
            <PreviewLink path="/robots.txt" label={t('seo.preview.robots')} />
            <Button
              type="primary"
              icon={<Save size={14} />}
              disabled={!dirty}
              loading={saveMutation.isPending}
              onClick={() => {
                if (draft) saveMutation.mutate(draft);
              }}
              data-testid="seo-save-btn"
            >
              {t('common:actions.save')}
            </Button>
          </Space>
        }
      />

      <Card>
        {isLoading || !draft ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <>
            {missingKeys ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('seo.missingKeys')}
              />
            ) : null}
            <Form layout="vertical" component="div">
              <Form.Item
                label={t('seo.fields.allowIndexingGlobal')}
                help={t('seo.fields.allowIndexingGlobalHint')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Switch
                    checked={draft.allowIndexingGlobal}
                    onChange={(v) =>
                      setDraft({ ...draft, allowIndexingGlobal: v })
                    }
                    data-testid="seo-allow-indexing"
                  />
                  <Text type={draft.allowIndexingGlobal ? 'success' : 'danger'}>
                    {draft.allowIndexingGlobal
                      ? t('seo.fields.allowIndexingOn')
                      : t('seo.fields.allowIndexingOff')}
                  </Text>
                </div>
              </Form.Item>

              <Form.Item
                label={t('seo.fields.sitemapUrl')}
                help={t('seo.fields.sitemapUrlHint')}
              >
                <Input
                  value={draft.sitemapUrl}
                  onChange={(e) =>
                    setDraft({ ...draft, sitemapUrl: e.target.value })
                  }
                  placeholder={`${apiBaseUrl}/sitemap.xml`}
                  data-testid="seo-sitemap-url"
                />
              </Form.Item>

              <Form.Item
                label={t('seo.fields.customBody')}
                help={t('seo.fields.customBodyHint')}
              >
                <Input.TextArea
                  value={draft.customBody}
                  onChange={(e) =>
                    setDraft({ ...draft, customBody: e.target.value })
                  }
                  rows={6}
                  spellCheck={false}
                  placeholder={'User-agent: *\nDisallow: /admin'}
                  style={{ fontFamily: 'var(--ds-font-mono, ui-monospace, monospace)' }}
                  data-testid="seo-custom-body"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Card>
    </div>
  );
}

export default SeoSettingsPage;
